import * as React from 'react';
import {
	AccountPreferences,
	File,
	FileVirusStatus,
	isFolder,
	isSymbolicLink,
	Item as ShareFileItem,
} from '@citrite/sf-api';
import { Checkbox, LoadingIconPrimary } from '@sharefiledev/flow-web';
import { ItemIcon } from '../ItemIcon';
import {
	StyledButtonWrapper,
	StyledCheckbox,
	StyledItem,
	StyledItemName,
	StyledItemNameCell,
} from './Item.styles';

export interface ItemProps {
	accountPreferences: AccountPreferences;
	disabled: boolean;
	initialItems: ShareFileItem[];
	isChecked: boolean;
	item: ShareFileItem;
	isRootLevelParentSymbolicLink?: boolean;
	onChecked(item: ShareFileItem): void;
	onClick(item: ShareFileItem): Promise<void>;
}

export interface State {
	isNavigating?: boolean;
}

export class Item extends React.Component<ItemProps, State> {
	public state: State = {};
	private rowRef = React.createRef<HTMLDivElement>();

	public render() {
		return (
			<StyledItem
				isChecked={this.props.isChecked}
				onClick={this.onChecked}
				tabIndex={this.showCheckbox() ? 0 : undefined}
				role="row"
				onKeyDown={this.onKeyDown}
				ref={this.rowRef}
			>
				<StyledCheckbox onClick={this.clickCheckbox} role="" data-checkbox-container>
					{this.showCheckbox() && (
						<Checkbox
							onChange={this.onChecked}
							checked={this.props.isChecked}
							disabled={this.isDisabled()}
							tabIndex={-1}
						/>
					)}
				</StyledCheckbox>
				<StyledButtonWrapper onClick={this.click} disabled={this.isDisabled()}>
					<StyledItemNameCell>
						{this.state.isNavigating ? (
							<LoadingIconPrimary />
						) : (
							<ItemIcon item={this.props.item} />
						)}
						<StyledItemName>{this.props.item.Name}</StyledItemName>
					</StyledItemNameCell>
				</StyledButtonWrapper>
			</StyledItem>
		);
	}

	private onKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
		if (e.key === ' ' && this.showCheckbox()) {
			e.preventDefault();
			this.onChecked();
		}
	};

	private clickCheckbox = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
	};

	private onChecked = () => {
		if (this.rowRef.current) {
			this.rowRef.current.blur();
		}
		if (this.isDisabled()) {
			return;
		}
		if (!this.showCheckbox()) {
			this.props
				.onClick(this.props.item)
				.then(() => this.setState({ isNavigating: true }));
			return;
		}
		this.props.onChecked(this.props.item);
	};

	private showCheckbox() {
		if (this.props.accountPreferences?.IsArchiveAccount) {
			return !isFolder(this.props.item);
		}
		const isConnectorFolder =
			isSymbolicLink(this.props.item) ||
			(isFolder(this.props.item) && this.props.isRootLevelParentSymbolicLink);
		const isRootLevelFolder = this.props.initialItems.some(
			item => item.Id === this.props.item.Id
		);
		return !isRootLevelFolder && !isConnectorFolder && !this.virusBlocksActions();
	}

	private isDisabled() {
		return this.props.disabled || this.virusBlocksActions();
	}

	private click = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (this.isDisabled()) {
			return;
		}
		if (!isFolder(this.props.item) && !isSymbolicLink(this.props.item)) {
			this.onChecked();
			return;
		}
		this.props.onClick(this.props.item).then(() => this.setState({ isNavigating: true }));
	};

	private virusBlocksActions() {
		return (
			this.hasVirus(this.props.item) && this.props.accountPreferences.VirusStrictness > 0
		);
	}

	private hasVirus(item: File) {
		return item.VirusStatus === FileVirusStatus.VirusWarning;
	}
}
